/**
 * `<ScreenerBuilder>` — Columns, Filters and Format over one substrate.
 *
 * Three tabs, one idea: a row is a screener column, and the tabs differ only in
 * what trails it. They share `<FieldRef>` and `<ExprCell>`, so a column
 * reference means the same thing everywhere and there is one place to improve.
 *
 * Everything here emits `ScreenerConfig` patches. The workspace stays the source
 * of truth, so the same state replays on any renderer — which is what makes the
 * grid swappable (D18).
 *
 * `ColumnPicker` is deliberately **not** absorbed: a one-click visibility popover
 * is worth having without opening the full builder. The two cannot drift because
 * both read the same `buildUniverse` result and both emit `columns` patches, with
 * `toggleColumn` the single mutation for show/hide.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  ScreenerColumn,
  ScreenerConfig,
  ScreenerExpr,
  ScreenerFilterCondition,
  ScreenerFilterNode,
  SavedScreenerFilter,
} from "@qufolio/model";
import type { ScreenerColumnMeta, ScreenerField } from "@qufolio/transport";

import { ExprCell } from "./ExprCell.js";
import { FormatTab } from "./FormatTab.js";
import { FieldRef } from "./FieldRef.js";
import { SavedFilterPicker } from "./SavedFilterPicker.js";
import { PresetsTab } from "./PresetsTab.js";
import { CopyFromScreen } from "./CopyFromScreen.js";
import { LinkFromScreen } from "./LinkFromScreen.js";
import {
  columnLinkKey,
  isLinkedColumn,
  linkColumns,
  linkConditions,
  linkLabel,
  unlinkColumn,
  type ScreenLinks,
} from "../lib/screen-links.js";
import { requestFit } from "../lib/fit-request.js";
import {
  copyColumns,
  copyConditions,
  type CopyFromScreen as CopyFromScreenBinding,
} from "../lib/copy-from-screen.js";
import {
  addLogo,
  buildUniverse,
  canAddLogo,
  mergedMeta,
  moveColumn,
  timeframeBadge,
  toggleColumn,
  type UniverseColumn,
} from "../lib/column-universe.js";
import { Badge, ColumnPicker } from "./ColumnPicker.js";
import { ConditionPresetList } from "./ConditionPresetList.js";
import { presetCopy, type PresetGate } from "../lib/condition-presets.js";
import { collectFields, needsHistory, printExpr } from "../lib/expr.js";
import { DEFAULT_PRECISION, defaultNotation, effectiveNotation } from "../lib/notation.js";
import { categoryLookup, columnBand } from "../lib/bands.js";
import { renameColumn, renameProblem, type RenameProblem } from "../lib/rename.js";
import {
  applySelection,
  categoriesOf,
  isSealed,
  moveCategory,
  selectedFilterIds,
  toggleFavorite,
} from "../lib/saved-filters.js";
import { NOTATIONS } from "../lib/notation.js";
import { summarizeFilter } from "../lib/filter-eval.js";
import { themeCssVars } from "../lib/theme.js";
import type { TokenResolver } from "../lib/columns.js";
import type { ScreenerConfigPatch } from "./ScreenerGrid.js";
import type { CompileExpr, DescribeColumn, DescribeExpr } from "../lib/compile.js";
import type { DslCatalog } from "../lib/dsl-catalog.js";

export type BuilderTab = "columns" | "filters" | "format" | "presets";

import { ScreenerTimingsPanel } from "./ScreenerTimings.js";
import type { ScreenerTimings } from "../lib/timings.js";

export interface ScreenerBuilderProps {
  /**
   * What the last screen cost, per stage — the `i` beside the add button.
   *
   * Read off the response's `timings` frame by the host, because the frames
   * arrive at the panel and this editor is mounted as its sibling. Omitted, the
   * button is not offered: a server that does not report timings should show no
   * control rather than an empty panel.
   */
  timings?: ScreenerTimings;
  /**
   * Run the load simulation and report what it cost — admin only.
   *
   * A button rather than a script, because the question it answers ("does a
   * screener rebuild starve the chart path?") is asked against a *running*
   * machine and the answer changes with the market phase. Omitted for everyone
   * who is not an admin, so the control does not exist rather than being
   * offered and refused.
   */
  onSimulate?: () => void;
  /** True while a simulation is in flight, so the button can say so. */
  simulating?: boolean;
  config: ScreenerConfig;
  fields: readonly ScreenerField[];
  meta?: readonly ScreenerColumnMeta[];
  onConfigChange: (patch: ScreenerConfigPatch) => void;
  /** Library mutations — separate from section patches. */
  onSavedFiltersChange?: (filters: SavedScreenerFilter[]) => void;
  /** ★ and group order — see `ScreenerPanelProps.canEditLibrary`. */
  canEditLibrary?: boolean;
  /** Which gated condition presets this caller is offered (`ConditionPreset.requires`). */
  presetGate?: PresetGate;
  onClose: () => void;
  initialTab?: BuilderTab;
  /** Mints a ULID. Injected so this package needs no id dependency. */
  mintId: () => string;
  /**
   * Resolves semantic color tokens (D13). Needed here as well as on the panel
   * because a docked builder is mounted by the host directly, as a sibling of
   * the screener rather than a child — so it cannot inherit the panel's CSS
   * variables and has to publish its own.
   */
  resolveToken?: TokenResolver;
  /**
   * `"modal"` floats over the grid with a dismissing backdrop; `"embedded"`
   * fills whatever container it is given, for docking into a layout column.
   *
   * Only the chrome differs — the three tabs are identical — so a docked editor
   * and a popped-open one can never drift apart.
   */
  variant?: "modal" | "embedded";
  /**
   * Compiles DSL text into an expression (see `compile.ts`). Injected, because
   * the parser is server-side and this island owns no transport. Omitted, every
   * expression cell is structured-only — which is exactly what it was before.
   */
  compileExpr?: CompileExpr;
  /** The DSL vocabulary, for the editor's reference panel. */
  dslCatalog?: DslCatalog;
  /** English -> DSL. Adds a Describe mode to the expression editor. */
  describeExpr?: DescribeExpr;
  /**
   * The Columns tab's split: the share of the height the column **list** takes,
   * leaving the rest to the expression editor below it.
   *
   * Read once, as the starting position — the divider owns it while it is being
   * dragged, or every pointer move would round-trip through the host and the
   * handle would lag the cursor. Omitted, the tab keeps its own and the split
   * simply does not survive a reload.
   */
  columnsSplit?: number;
  onColumnsSplit?: (split: number) => void;
  /**
   * Publishes the current screen as a preset.
   *
   * Supplied by the host only for somebody the server would allow, so the
   * control is absent rather than present-and-refused — the same rule the app
   * menu follows for its admin rows.
   */
  publishPreset?: (input: {
    name: string;
    description: string;
    columns: ScreenerConfig["columns"];
    filter: ScreenerConfig["filter"];
    sort: ScreenerConfig["sort"];
    /** A Recs or TopDown screen keeps being one when published (omitted: ordinary). */
    recs?: ScreenerConfig["recs"];
    topDown?: ScreenerConfig["topDown"];
    seasonality?: ScreenerConfig["seasonality"];
  }) => Promise<void>;
  /** What the group has published, with History — see `PresetsTab`. */
  publishedSlot?: ReactNode;
  /**
   * **Copy from screen…** in the Columns and Conditions tabs: the screens the
   * author can read, and a loader for one (`lib/copy-from-screen.ts`).
   *
   * Injected because this island fetches nothing, and passed by the host **only
   * while authoring** — so, like `publishPreset`, the control is absent rather
   * than offered to somebody it is not for.
   */
  copyFromScreen?: CopyFromScreenBinding;
  /** Whose sealed conditions these are (`JP`) — `SavedFilterPickerProps.sealedBy`. */
  sealedBy?: string;
  /**
   * **Link from screen…**, the 🔗 marks, Unlink and Remove
   * (`lib/screen-links.ts`). Offered to every member, for the screens of
   * groups they hold; `config` is then the materialised one, and the host
   * reduces every patch back to links before it is stored.
   */
  screenLinks?: ScreenLinks;
}

/** List over editor, as it was before the divider existed. */
export const DEFAULT_COLUMNS_SPLIT = 0.75;

/**
 * Neither pane may be dragged away entirely.
 *
 * The editor is where the work happens and the list is what says which column
 * the work applies to, so a split that hides either leaves a tab that cannot be
 * used — and, unlike a closed panel, nothing on screen would say why.
 */
function clampSplit(split: number): number {
  if (!Number.isFinite(split)) return DEFAULT_COLUMNS_SPLIT;
  return Math.min(0.85, Math.max(0.2, split));
}

const TABS: { id: BuilderTab; label: string }[] = [
  { id: "columns", label: "Columns" },
  { id: "filters", label: "Conditions" },
  { id: "format", label: "Format" },
];

/**
 * Publishing last, and only for somebody who can.
 *
 * It was "Presets", and it did two jobs: a list that *copied* a club's screen
 * into the panel, and the form that publishes the panel to a club. The copy has
 * moved to the club tab it applies to, so what is left is one act and the tab
 * is named for it — and, like every other control of its kind here, it is
 * absent rather than present and refused for an account that cannot publish.
 */
const PRESETS_TAB: { id: BuilderTab; label: string } = { id: "presets", label: "Publish" };

export function ScreenerBuilder({
  config,
  fields,
  meta = [],
  onConfigChange,
  onSavedFiltersChange,
  canEditLibrary = true,
  presetGate,
  onClose,
  initialTab = "columns",
  mintId,
  resolveToken,
  variant = "modal",
  compileExpr,
  dslCatalog,
  describeExpr,
  columnsSplit,
  onColumnsSplit,
  publishPreset,
  publishedSlot,
  copyFromScreen,
  sealedBy,
  screenLinks,
  timings,
  onSimulate,
  simulating,
}: ScreenerBuilderProps) {
  const [tab, setTab] = useState<BuilderTab>(initialTab);
  // Ephemeral (D8): a reopened workspace must not reopen a perf panel.
  const [showTimings, setShowTimings] = useState(false);

  // Appended rather than declared in `TABS`, because whether it exists depends
  // on what the host injected — and a tab that renders "nothing here, and I
  // cannot say why" is worse than no tab.
  //
  // **Keyed on `publishPreset` now, not on the catalog.** The tab used to exist
  // whenever there were presets to *list*; the list has gone to the club tab,
  // so the only thing left in here is the publish form — and a Publish tab for
  // an account that cannot publish is an empty room with its name on the door.
  const canPreset = publishPreset !== undefined;
  const visibleTabs = canPreset ? [...TABS, PRESETS_TAB] : TABS;

  const universe = useMemo(
    () => buildUniverse({ fields, meta, columns: config.columns }),
    [fields, meta, config.columns],
  );

  /**
   * Server metadata plus the columns the user authored. Everything that picks a
   * field reads this, so a column becomes filterable and referenceable the
   * moment it exists rather than only after the server learns about it.
   */
  const fieldMeta = useMemo(() => mergedMeta(meta, config.columns), [meta, config.columns]);

  const embedded = variant === "embedded";

  const inner = (
    <div
      style={{ ...themeCssVars(resolveToken), ...(embedded ? embeddedPanel : panel) }}
      onClick={(e) => e.stopPropagation()}
    >
        <header style={header}>
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              // The underline does the work: a background tint alone is easy to
              // miss against a surface that is already tinted, and which tab you
              // are on is the first thing to read here.
              style={{
                ...tabButton,
                background: tab === t.id ? "var(--qf-selection)" : "transparent",
                color: tab === t.id ? "var(--qf-text)" : "var(--qf-text-secondary)",
                fontWeight: tab === t.id ? 600 : 400,
                boxShadow:
                  tab === t.id ? "inset 0 -2px 0 var(--qf-accent)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
          {/*
            **The `group/screen` pair was printed here and is not any more.**

            It named the *finset* the workspace was saved with — `SP500/JASDOTS`
            on every workspace on this machine — which is not what the panel is
            editing. The rows come from whichever group chips are selected, so
            the caption sat above a table it did not describe, naming a screen
            nobody had picked and in most cases one nobody had heard of.

            It survives where it is still true: `ScreenerView`'s "No group
            picked — falling back to …" says the pair *because* that is the state
            being reported, and saying it without the name is the bug that
            message was written to fix.
          */}
          <button onClick={onClose} style={{ ...tabButton, marginLeft: "auto" }}>
            ✕
          </button>
        </header>

        <div style={body}>
          {tab === "columns" && (
            <ColumnsTab
              config={config}
              universe={universe}
              meta={fieldMeta}
              onConfigChange={onConfigChange}
              mintId={mintId}
              compileExpr={compileExpr}
              dslCatalog={dslCatalog}
              describeExpr={describeExpr}
              columnsSplit={columnsSplit}
              onColumnsSplit={onColumnsSplit}
              timings={timings}
              showTimings={showTimings}
              onToggleTimings={() => setShowTimings((open) => !open)}
              onSimulate={onSimulate}
              simulating={simulating}
              copyFromScreen={copyFromScreen}
              screenLinks={screenLinks}
            />
          )}
          {/*
            These two scroll as one column; only the Columns tab splits itself,
            so only it manages its own scrolling. `body` stopped scrolling when
            it became the flex container that gives that split a height.
          */}
          {tab === "filters" && (
            <div style={scrollPane}>
              <FiltersTab
                config={config}
                meta={fieldMeta}
                onConfigChange={onConfigChange}
                onSavedFiltersChange={onSavedFiltersChange}
                canEditLibrary={canEditLibrary}
                presetGate={presetGate}
                mintId={mintId}
                scanScript={{ compile: compileExpr, catalog: dslCatalog }}
                copyFromScreen={copyFromScreen}
                sealedBy={sealedBy}
                screenLinks={screenLinks}
              />
            </div>
          )}
          {tab === "presets" && canPreset && (
            <div style={scrollPane}>
              <PresetsTab config={config} publishPreset={publishPreset} publishedSlot={publishedSlot} />
            </div>
          )}
          {tab === "format" && (
            <div style={scrollPane}>
              <FormatTab
                config={config}
                universe={universe}
                meta={fieldMeta}
                onConfigChange={onConfigChange}
                compileExpr={compileExpr}
                resolveToken={resolveToken}
              />
            </div>
          )}
      </div>
    </div>
  );

  // Embedded fills its container; modal floats over the grid with a backdrop
  // that dismisses on click.
  return embedded ? (
    inner
  ) : (
    <div style={backdrop} onClick={onClose}>
      {inner}
    </div>
  );
}

// ── Columns ─────────────────────────────────────────────────────────────────

/**
 * An editable field id: commits on blur or Enter, reverts on Escape.
 *
 * Held locally while typing, because a rename rewrites the whole config on
 * every commit — doing that per keystroke would rename to `m`, then `my`, then
 * `myR`, each one a refactor.
 */
function IdInput({
  value,
  onRename,
  problemFor,
}: {
  value: string;
  onRename: (next: string) => void;
  problemFor: (next: string) => RenameProblem | null;
}) {
  const [draft, setDraft] = useState(value);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setDraft(value);
    setTouched(false);
  }, [value]);

  const problem = touched ? problemFor(draft) : null;
  const commit = () => {
    if (!touched) return;
    if (problem || draft.trim() === value) {
      setDraft(value);
      setTouched(false);
      return;
    }
    onRename(draft.trim());
  };

  return (
    <input
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        setTouched(true);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") {
          setDraft(value);
          setTouched(false);
        }
      }}
      title={
        problem
          ? PROBLEM_TEXT[problem]
          : `Referenced in expressions as ${value}. Renaming updates every reference.`
      }
      style={{
        ...inputStyle,
        width: "100%",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.72rem",
        borderColor: problem ? "var(--qf-negative)" : "var(--qf-border)",
      }}
    />
  );
}

const PROBLEM_TEXT: Record<RenameProblem, string> = {
  empty: "A column needs a name.",
  invalid: "Letters, digits and underscore only, not starting with a digit.",
  taken: "Another column already has that name.",
};

function ColumnsTab({
  config,
  universe,
  meta,
  onConfigChange,
  mintId,
  compileExpr,
  dslCatalog,
  describeExpr,
  columnsSplit,
  onColumnsSplit,
  timings,
  showTimings,
  onToggleTimings,
  onSimulate,
  simulating,
  copyFromScreen,
  screenLinks,
}: {
  config: ScreenerConfig;
  universe: UniverseColumn[];
  meta: readonly ScreenerColumnMeta[];
  onConfigChange: (patch: ScreenerConfigPatch) => void;
  mintId: () => string;
  compileExpr?: CompileExpr;
  dslCatalog?: DslCatalog;
  describeExpr?: DescribeExpr;
  columnsSplit?: number;
  onColumnsSplit?: (split: number) => void;
  timings?: ScreenerTimings;
  showTimings: boolean;
  onToggleTimings: () => void;
  onSimulate?: () => void;
  simulating?: boolean;
  copyFromScreen?: CopyFromScreenBinding;
  screenLinks?: ScreenLinks;
}) {
  const columns = config.columns ?? [];

  /**
   * The divider's position, owned here rather than by the host.
   *
   * Seeded from the prop and never re-read: the handle has to track the cursor,
   * and a position that round-tripped through the host on every pointer move
   * would render a frame behind the gesture — and would snap back on release,
   * since the parent's state arrives a tick after the commit. The host gets one
   * call when the drag ends, which is the only value worth storing.
   */
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [split, setSplit] = useState(() => clampSplit(columnsSplit ?? DEFAULT_COLUMNS_SPLIT));

  const dragTo = (clientY: number): void => {
    const box = layoutRef.current?.getBoundingClientRect();
    if (!box || box.height === 0) return;
    setSplit(clampSplit((clientY - box.top) / box.height));
  };

  /** Arrow keys move it too — a divider reachable only by pointer is not one. */
  const nudge = (delta: number): void => {
    setSplit((current) => {
      const next = clampSplit(current + delta);
      onColumnsSplit?.(next);
      return next;
    });
  };
  /**
   * Which computed column the editor below is bound to. Ephemeral view state
   * (D8) — a workspace records the columns, never which one happened to be open.
   */
  const [selected, setSelected] = useState<string | null>(null);
  /** The column being dragged, by field. Null when no drag is in flight. */
  const [dragField, setDragField] = useState<string | null>(null);

  /**
   * **The screen's own columns, and nothing else.**
   *
   * This listed the entire universe — every field the server sends, ninety-odd
   * of them, each with an unticked box. That makes the tab a picker, and a
   * picker is what the Columns popover already is. The cost was that the one
   * surface for *editing* a screen's columns — their headers, widths, pins,
   * decimals and expressions — buried the five you had among ninety you had
   * not chosen, and there was no way to stop it.
   *
   * So the rows are the configured columns, shown and hidden both: a hidden one
   * still has a header and a width you set, and dropping it from the list would
   * strand them. Adding a column stays where it already was, in the popover.
   *
   * **An empty config still lists everything**, because a screen that has
   * chosen nothing has no other way in, and the grid is showing every column
   * anyway — which is the state this list is the fix for.
   *
   * The order below: what is on screen, in its order, then everything else.
   *
   * **`buildUniverse` puts every *configured* column first, and that is not the
   * same thing.** A column can be configured and hidden — it has a header or a
   * width someone set, and a `hide`. Those sat in the top block among the shown
   * ones, so the block read as "what is on screen" while containing rows that
   * are not, and giving a hidden column any property at all moved its row up
   * into it.
   *
   * Sorted by `order`, which `buildUniverse` sets to the position among *visible*
   * columns and to `Infinity` for the rest — ties broken by the order it built
   * them in, so the available columns keep the server's listing and nothing
   * swaps places between renders.
   */
  const listed = useMemo(() => {
    const ordered = universe
      .map((u, at) => ({ u, at }))
      .sort((a, b) => a.u.order - b.u.order || a.at - b.at)
      .map((entry) => entry.u);
    // On screen only (owner, 2026-10-26). A hidden column keeps everything set
    // on it and comes back through + Add columns; listing it here put ~120
    // unticked rows under the eight a screen shows.
    return ordered.filter((u) => u.visible);
  }, [universe]);

  /** ✎ Edit: the per-column settings, shown only when asked for. View state (D8). */
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  /**
   * What the describer is told exists: the screen's columns with their
   * meanings, plus the user's own with their definitions.
   *
   * Assembled here because this is the only place holding both — `meta` comes
   * from the server and the computed ones live in the config. Sending names
   * alone would leave "momentum" unable to find `jforce2`, and a column the
   * user built five minutes ago invisible entirely.
   */
  const describeColumns = useMemo<DescribeColumn[]>(() => {
    const fromServer = meta.map((m) => ({
      field: m.field,
      label: m.label,
      description: m.description,
    }));
    const authored = (config.columns ?? [])
      .filter((c) => c.computed)
      .map((c) => ({ field: c.field, computed: c.computed!.source ?? "" }));
    return [...fromServer, ...authored];
  }, [meta, config.columns]);

  /**
   * Change one property of one column, adding an entry if there was none.
   *
   * **An added entry keeps the column hidden, and that is a bug fix rather than
   * a nicety.** A field the view does not list is *available but not shown*;
   * appending `{ field, header }` for it gave a column with no `hide`, and
   * `hide !== true` is what "visible" means — so typing a header on a column
   * that was off silently switched it on, moved its row up into the shown block,
   * and shifted every row beneath it under the pointer mid-keystroke.
   *
   * `toggleColumn` is the one path that *should* change whether a column shows,
   * because that is the control the user aimed at.
   */
  const [colorByOpen, setColorByOpen] = useState(false);
  const patch = (field: string, changes: Partial<ScreenerColumn>) => {
    const existing = columns.find((c) => c.field === field);
    if (existing) {
      onConfigChange({
        columns: columns.map((c) => (c.field === field ? { ...c, ...changes } : c)),
      });
      return;
    }
    const shown = universe.find((u) => u.field === field)?.visible ?? true;
    onConfigChange({
      columns: [...columns, { field, ...(shown ? {} : { hide: true }), ...changes }],
    });
  };

  /**
   * Turn a server column into a computed one, seeded with itself.
   *
   * The id is kept, so every filter and expression already naming it keeps
   * working — what changes is where the value comes from. Seeding with the
   * field rather than a blank means the common edit ("this, but scaled") starts
   * from something true instead of from zero.
   */
  /** Ids the server owns — a computed column may not shadow one. */
  const serverFields = useMemo(() => universe.map((u) => u.field), [universe]);

  // ── bands ────────────────────────────────────────────────────────────────
  const bandListId = useId();
  const metaByField = useMemo(() => new Map(meta.map((m) => [m.field, m])), [meta]);
  const categoryOf = useMemo(() => categoryLookup(dslCatalog), [dslCatalog]);
  /** The default band, as the grid would draw it: `w Momentum`, `Price`. */
  const bandPlaceholder = (field: string): string => {
    const column = columns.find((c) => c.field === field);
    const { band: _typed, ...untyped } = column ?? { field };
    const band = columnBand(untyped, metaByField.get(field), categoryOf, (f) => metaByField.get(f));
    return band ? [band.period, band.name].filter(Boolean).join(" ") : "—";
  };
  /** Names already in use on this screen, typed or default, for the picker. */
  const bandNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of columns) {
      const band = columnBand(c, metaByField.get(c.field), categoryOf, (f) => metaByField.get(f));
      if (band?.name) names.add(band.name);
    }
    return [...names].sort();
  }, [columns, metaByField, categoryOf]);

  /** Rename a computed column, carrying every reference with it. */
  const rename = (from: string, to: string) => {
    const next = renameColumn(config, from, to);
    onConfigChange({
      columns: next.columns,
      ...(next.filter ? { filter: next.filter } : {}),
      ...(next.sort ? { sort: next.sort } : {}),
    });
    if (selected === from) setSelected(to);
  };

  const promote = (field: string) => {
    setSelected(field);
    patch(field, {
      computed: {
        expression: { kind: "field", field },
        source: field,
        requires: [field],
        rollup: "recompute",
      },
    });
  };

  const addComputed = () => {
    const field = `computed${columns.filter((c) => c.computed).length + 1}`;
    // Select it immediately: the point of adding one is to write its expression.
    setSelected(field);
    onConfigChange({
      columns: [
        ...columns,
        {
          field,
          header: "New column",
          // A literal is the neutral starting point; the user replaces it.
          computed: { expression: { kind: "literal", value: 0 }, rollup: "recompute" },
        },
      ],
    });
    void mintId; // ids are minted for filters, not columns (field is the key)
  };

  return (
    // Three quarters list, one quarter editor. The editor is pinned rather than
    // scrolling with the list: it is where the work happens, and having to
    // scroll away from the column you just selected to reach its expression is
    // what made the previous layout unusable.
    <div style={columnsLayout} ref={layoutRef}>
      <div style={{ ...listPane, flex: `${split} 1 0` }}>
      <Toolbar>
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{ ...smallButton, background: "var(--qf-accent)", color: "var(--qf-background)", borderColor: "var(--qf-accent)" }}
        >
          + Add columns
        </button>
        {/*
          Clearing widths is what *enables* auto-sizing rather than a separate
          mode: a column with no width is measured from its data, and one with a
          width keeps it. So "fit to data" is simply forgetting the widths.
        */}
        {/*
          A command to the grid showing this panel (`lib/fit-request.ts`): it
          measures now and keeps the widths. With no grid on screen, the old
          route — forget the widths, so the grid measures them when it opens.
        */}
        <button
          onClick={() => {
            if (!requestFit(config.id, "data")) {
              onConfigChange({ columns: columns.map(({ width: _width, ...rest }) => rest) });
            }
          }}
          title="Size every column to its values"
          style={smallButton}
        >
          Fit to data
        </button>
        <button
          onClick={() => {
            if (!requestFit(config.id, "names")) {
              onConfigChange({ columns: columns.map(({ width: _width, ...rest }) => rest) });
            }
          }}
          title="Size every column to its values and its name, so no name is cut"
          style={smallButton}
        >
          Fit to names
        </button>
        {/* Which hours count as live for the header marks — the yellow and
            blue lines (docs/plans/screener-bands.md). */}
        <label
          title="Mark live and computed column headers 04:00-20:00 ET instead of 09:30-16:00"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.8rem" }}
        >
          <input
            type="checkbox"
            checked={config.extendedHours === true}
            onChange={(e) => onConfigChange({ extendedHours: e.target.checked || undefined })}
          />
          Pre/post market
        </label>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setEditing((on) => !on)}
          aria-pressed={editing}
          title="Header, band, width, pin, decimals and scale for each column"
          style={smallButton}
        >
          {editing ? "✓ Done" : "✎ Edit"}
        </button>
      </Toolbar>

      <table style={editing ? table : { ...table, minWidth: 0 }}>
        <thead>
          <tr>
            {/* Unlabelled: a grip and a delete are affordances rather than
                fields, and a header over them would read as a column of data. */}
            <Th w={COLUMN_WIDTHS.grip} />
            {/* The name, read only, first in both modes; ✎ Edit adds the
                settings after it, each showing its default as a placeholder. */}
            <Th w={editing ? COLUMN_WIDTHS.name : undefined}>Name</Th>
            {editing && (
              <>
            {/* Three distinct names, and they are not interchangeable: the
                header is what you read, the column is what an expression
                references, and the DSL is what produces the value. Merging any
                two of them is how a column ends up known by a name nothing can
                use. */}
            <Th w={COLUMN_WIDTHS.header}>Header</Th>
            <Th w={COLUMN_WIDTHS.band}>Band</Th>
            <Th w={COLUMN_WIDTHS.column}>Column</Th>
            {/* Unsized on purpose: DSL takes whatever the others leave, so it
                gets the whole of a wide panel. `table.minWidth` is what stops
                that becoming zero on a narrow one. */}
            <Th>ScanScript</Th>
            <Th w={COLUMN_WIDTHS.width}>Width</Th>
            <Th w={COLUMN_WIDTHS.pin}>Pin</Th>
            <Th w={COLUMN_WIDTHS.decimals}>Decimals</Th>
            <Th w={COLUMN_WIDTHS.scale}>Scale</Th>
              </>
            )}
            {!editing && <Th w={COLUMN_WIDTHS.scale}>Format</Th>}
            <Th w={COLUMN_WIDTHS.grip} />
          </tr>
        </thead>
        <tbody>
          {listed.map((u, index) => {
            const column = columns.find((c) => c.field === u.field);
            // **A linked row is the group's but for its place, width, pin and
            // whether it shows** (`screen-links.ts`): every other cell is read
            // only, and the Column cell names the source with Unlink — or, for
            // a placeholder, says it is gone, with Remove.
            const linked = isLinkedColumn(column) ? column : undefined;
            const gone = !!linked && !!screenLinks?.missing.has(columnLinkKey(linked));
            return (
              <tr
                key={u.field}
                // **The whole row selects a computed column**, not just the DSL
                // cell. That cell is one truncated line in a fixed-layout table
                // and gets narrower as the panel does, so on a narrow screener
                // it is the smallest target in the row — and it was the only one
                // that opened the editor.
                //
                // Interactive descendants are excluded rather than each being
                // given a `stopPropagation`: there are seven of them per row, a
                // new one would silently start selecting, and `closest` states
                // the rule once where it can be read.
                // No `aria-label` here on purpose: the DSL cell's button already
                // carries "Edit expression for …", and a second element with the
                // same accessible name makes `getByLabelText` ambiguous — for a
                // test and for a screen reader alike. The row is a wider mouse
                // target for a control that already exists, not a new control.
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("input,select,textarea,button")) return;
                  setSelected(u.field);
                }}
                // **Focus selects too, and that is not the same rule twice.**
                // The click handler above excludes every input, because typing
                // in the Header box must not be swallowed — but Header is the
                // widest cell and the most natural place to click, so a
                // computed row could be clicked squarely and never open its
                // expression. Focusing any control inside the row now selects
                // it as well: the cell stays editable *and* the editor opens,
                // which is what "I clicked the column" meant all along.
                onFocusCapture={() => {
                  // **The selection follows the row, whatever kind it is.**
                  // Binding only to computed rows meant a plain row either left
                  // the panel showing the previous column — an expression
                  // captioned with somebody else's — or emptied it to "select a
                  // computed column above", which is unhelpful advice to
                  // somebody who has plainly selected a column. The panel now
                  // answers for whatever row you are on; what it offers for a
                  // server column is the way to give it an expression.
                  setSelected(u.field);
                }}
                onDragOver={(event) => {
                  // `preventDefault` is what *permits* a drop — without it the
                  // browser refuses every one, silently, and the drag ends with
                  // nothing happening and no error.
                  if (dragField === null || !u.visible) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  if (dragField === null || !u.visible) return;
                  event.preventDefault();
                  const next = moveColumn(columns, dragField, u.field);
                  if (next !== columns) onConfigChange({ columns: [...next] });
                  setDragField(null);
                }}
                // The same striping and canvas the data grid uses: this is a
                // table of the very columns shown there, and two tables side by
                // side in different colours read as two unrelated things.
                // `row.alt` and `background` are the grid's own tokens, so a
                // theme switch moves both together.
                style={{
                  borderTop: "1px solid var(--qf-border)",
                  background:
                    selected === u.field
                      ? "var(--qf-surface-alt)"
                      : index % 2 === 1
                        ? "var(--qf-row-alt)"
                        : "var(--qf-background)",
                  // Only a computed row has an editor to open, so only it offers
                  // the affordance. A server column's row stays inert.
                  cursor: column?.computed ? "pointer" : "default",
                }}
              >
                <Td>
                  {/* **Only a shown column has a place in the order**, so only
                      one gets a grip. The list is the configured columns
                      followed by everything the server offers, and dragging
                      inside the second half would be asking to reorder a
                      catalogue. */}
                  {u.visible ? (
                    <span
                      draggable
                      role="button"
                      tabIndex={-1}
                      aria-label={`Reorder ${u.header}`}
                      title="Drag to reorder"
                      onDragStart={(event) => {
                        setDragField(u.field);
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", u.field);
                      }}
                      onDragEnd={() => setDragField(null)}
                      style={GRIP}
                    >
                      ⠿
                    </span>
                  ) : null}
                </Td>
                <Td>
                  <span
                    title={u.description || u.field}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0, maxWidth: "100%" }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.header}
                    </span>
                    {u.timeframe && <Badge>{timeframeBadge(u.timeframe)}</Badge>}
                    {linked && <span title={`Linked from ${linkLabel(screenLinks, linked.link.screen)}`}>🔗</span>}
                  </span>
                </Td>
                {editing && (
                  <>
                <Td>
                  <input
                    value={column?.header ?? ""}
                    placeholder={u.header}
                    title={linked ? `${linkLabel(screenLinks, linked.link.screen)}'s header` : u.description}
                    disabled={!!linked}
                    onChange={(e) => patch(u.field, { header: e.target.value || undefined })}
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </Td>
                <Td>
                  {/* The band over this column's header (docs/plans/
                      screener-bands.md). Empty means the default, which the
                      placeholder shows — its category, after its period. */}
                  <input
                    value={column?.band ?? ""}
                    placeholder={bandPlaceholder(u.field)}
                    list={bandListId}
                    title="Header band — type a name or pick one; empty uses the category"
                    disabled={!!linked}
                    onChange={(e) => patch(u.field, { band: e.target.value || undefined })}
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </Td>
                <Td>
                  {linked ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                      {gone ? (
                        <span style={{ color: "var(--qf-warn)", overflow: "hidden", textOverflow: "ellipsis" }}>
                          ⚠ no longer in {screenLinks?.describe(linked.link.screen)?.name ?? linked.link.screen}
                        </span>
                      ) : (
                        <span
                          title={`Linked from ${linkLabel(screenLinks, linked.link.screen)} — its format is that screen's`}
                          style={{ color: "var(--qf-text-secondary)", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          🔗 {linkLabel(screenLinks, linked.link.screen)}
                        </span>
                      )}
                      {gone ? (
                        <button
                          type="button"
                          aria-label={`Remove ${u.header}`}
                          onClick={() =>
                            onConfigChange({ columns: columns.filter((c) => c.field !== u.field) })
                          }
                          style={smallButton}
                        >
                          Remove
                        </button>
                      ) : (
                        // A column may be unlinked: it becomes this screen's
                        // own, formatting and all — nothing secret travels.
                        <button
                          type="button"
                          aria-label={`Unlink ${u.header}`}
                          title="Make it this screen's own column, with the formatting it has now"
                          onClick={() => onConfigChange({ columns: unlinkColumn(columns, u.field) })}
                          style={smallButton}
                        >
                          Unlink
                        </button>
                      )}
                    </span>
                  ) : column?.computed ? (
                    // A computed column's id is the user's — there is no server
                    // field behind `computed1`, and nobody chose that name.
                    // Renaming rewrites every reference, so it is a refactor
                    // rather than a relabel.
                    <IdInput
                      value={u.field}
                      onRename={(next) => rename(u.field, next)}
                      problemFor={(next) =>
                        renameProblem(config, u.field, next, serverFields)
                      }
                    />
                  ) : (
                    // Read-only: a server column's id has to match the schema it
                    // arrives under, so renaming would break the binding rather
                    // than move it.
                    <code title={`Referenced in expressions as ${u.field}`} style={idCell}>
                      {u.field}
                    </code>
                  )}
                </Td>
                <Td>
                  {linked ? (
                    // The source's, and not this screen's to change.
                    <span style={{ ...exprSummary, color: "var(--qf-text-muted)", fontStyle: "italic" }}>
                      {u.field}
                    </span>
                  ) : column?.computed ? (
                    // A summary that selects, not an editor. An expression
                    // belongs somewhere it can be read and corrected; a table
                    // cell gives it one line, wraps its controls and leaves an
                    // error nowhere to go.
                    <button
                      onClick={() => setSelected(u.field)}
                      aria-label={`Edit expression for ${u.field}`}
                      // The tooltip is the expression itself: the cell shows one
                      // truncated line, and the whole point of hovering is to
                      // read what the column actually computes.
                      title={column.computed.source || printExpr(column.computed.expression)}
                      style={{
                        ...exprSummary,
                        borderColor:
                          selected === u.field ? "var(--qf-accent)" : "var(--qf-border)",
                        color: selected === u.field ? "var(--qf-text)" : "var(--qf-accent)",
                      }}
                    >
                      {column.computed.source || printExpr(column.computed.expression) || "—"}
                    </button>
                  ) : (
                    // Greyed, because a server column has no expression — its
                    // value *is* the field. Editing one promotes it to computed,
                    // seeded with itself, so "close but ×100" starts from close
                    // rather than from nothing.
                    <button
                      onClick={() => promote(u.field)}
                      title={`${u.field} — click to compute this column from an expression`}
                      style={{ ...exprSummary, color: "var(--qf-text-muted)", fontStyle: "italic" }}
                    >
                      {u.field}
                    </button>
                  )}
                </Td>
                <Td>
                  <input
                    type="number"
                    value={column?.width ?? ""}
                    onChange={(e) =>
                      patch(u.field, { width: e.target.value ? Number(e.target.value) : undefined })
                    }
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </Td>
                <Td>
                  <select
                    value={column?.pinned ?? ""}
                    onChange={(e) =>
                      patch(u.field, {
                        pinned: (e.target.value || undefined) as ScreenerColumn["pinned"],
                      })
                    }
                    style={{ ...inputStyle, width: "100%" }}
                  >
                    <option value="">—</option>
                    <option value="left">left</option>
                    <option value="right">right</option>
                  </select>
                </Td>
                <Td>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={column?.precision ?? ""}
                    // **The placeholder is the default the grid will actually
                    // use**, not a suggestion. A number column with nothing here
                    // renders at two decimals, so an empty box that said nothing
                    // left the one question this cell answers unanswered — and
                    // `0` typed in it is a real choice, which is why the default
                    // is shown greyed rather than filled in.
                    placeholder={String(DEFAULT_PRECISION)}
                    disabled={!!linked}
                    title="Decimal places. Empty uses the default."
                    onChange={(e) =>
                      patch(u.field, {
                        precision: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </Td>
                <Td>
                  {/* Presentation only: the value stays a number, so the column
                      still sorts and filters by magnitude while reading as
                      3.25B. */}
                  <select
                    value={effectiveNotation(column, u.baseField) ?? "plain"}
                    onChange={(e) => {
                      // Absence means the field's default (K / M / B for a
                      // volume), so picking that default stores nothing and
                      // picking Plain on a volume stores "plain".
                      const picked = e.target.value as NonNullable<ScreenerColumn["notation"]>;
                      const fallback = defaultNotation(u.baseField) ?? "plain";
                      patch(u.field, { notation: picked === fallback ? undefined : picked });
                    }}
                    title="Abbreviate large numbers — display only"
                    disabled={!!linked}
                    style={{ ...inputStyle, width: "100%" }}
                  >
                    {NOTATIONS.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </Td>
                  </>
                )}
                {!editing && (
                  <Td>
                    <span style={{ color: "var(--qf-text-muted)", fontSize: "0.72rem" }}>
                      {notationShort(effectiveNotation(column, u.baseField))}
                    </span>
                  </Td>
                )}
                <Td>
                  {/* Removes it from the screen, which is hiding it: the column
                      keeps its header, width and expression, and + Add columns
                      brings it back. A computed column is deleted from the
                      expression editor's Delete, the one place that says so. */}
                  <button
                    type="button"
                    // Not "Remove …": a gone link's placeholder has a Remove
                    // that deletes the entry, and the two must not share a name.
                    aria-label={`Take ${u.header} off the screen`}
                    title={`Take ${u.header} off the screen`}
                    onClick={() => {
                      onConfigChange({ columns: toggleColumn(universe, columns, u.field, false) });
                      if (selected === u.field) setSelected(null);
                    }}
                    style={smallButton}
                  >
                    ✕
                  </button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <datalist id={bandListId}>{bandNames.map((name) => <option key={name} value={name} />)}</datalist>
      {adding && (
        <ColumnPicker
          universe={universe}
          columns={columns}
          initialTab="browse"
          onChange={(next) => onConfigChange({ columns: next })}
          onClose={() => setAdding(false)}
        />
      )}
      </div>

      {/*
        The divider. Pointer capture rather than window listeners, so a drag
        that outruns the handle still tracks — and still ends — on a pointer the
        element no longer sits under.
      */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize the expression editor"
        aria-valuenow={Math.round(split * 100)}
        aria-valuemin={20}
        aria-valuemax={85}
        tabIndex={0}
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          dragTo(event.clientY);
        }}
        onPointerUp={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          event.currentTarget.releasePointerCapture(event.pointerId);
          onColumnsSplit?.(split);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp") nudge(-0.02);
          else if (event.key === "ArrowDown") nudge(0.02);
          else return;
          event.preventDefault();
        }}
        style={splitHandle}
      >
        <div style={splitGrip} />
      </div>

      <div style={{ ...editorPane, flex: `${1 - split} 1 0` }}>
      <ExpressionEditor
        // Never a linked one: its expression, if it has one, is the source's.
        column={columns.find((c) => c.field === selected && c.computed && !c.link)}
        // Without `selected` excluded, a column's own expression could name
        // itself — which compiles and then recurses.
        meta={mergedMeta(meta, config.columns, selected ?? undefined)}
        compileExpr={compileExpr}
        dslCatalog={dslCatalog}
        describeExpr={describeExpr}
        describeColumns={describeColumns}
        onChange={(expression, source) => {
          if (!selected) return;
          const current = columns.find((c) => c.field === selected);
          if (!current?.computed) return;
          // `source` is dropped rather than carried forward when a structural
          // edit produced this: text that no longer describes the expression
          // would reopen the editor showing something the column does not
          // compute.
          const { source: _replaced, ...kept } = current.computed;
          patch(selected, {
            computed: {
              ...kept,
              expression,
              ...(source ? { source } : {}),
              // Kept in step so a screen missing an input skips the column
              // instead of showing a column of nulls.
              requires: [...collectFields(expression)],
            },
          });
        }}
        onRollupChange={(rollup) => {
          if (!selected) return;
          const current = columns.find((c) => c.field === selected);
          if (!current?.computed) return;
          patch(selected, { computed: { ...current.computed, rollup } });
        }}
        onDelete={() => {
          if (!selected) return;
          // Removed outright rather than hidden. A computed column exists only
          // because it is in this array — there is no upstream field to fall
          // back to — so hiding it would leave an invisible entry that nothing
          // can reach and the picker would still list.
          onConfigChange({ columns: columns.filter((c) => c.field !== selected) });
          setSelected(null);
        }}
        onAdd={addComputed}
        // Absent rather than disabled once a logo exists: the button's only job
        // is to add the one column, so after the click it has nothing to say.
        onAddLogo={
          canAddLogo(universe, columns)
            ? () => onConfigChange({ columns: addLogo(universe, columns) })
            : undefined
        }
        selectedField={selected}
        onPromote={columns.some((c) => c.field === selected && c.link) ? undefined : promote}
        // Beside + Column and + Logo (mock 3): the third way a column arrives.
        copySlot={
          copyFromScreen || screenLinks ? (
            <>
              {copyFromScreen && (
                <CopyFromScreen
                  kind="columns"
                  source={copyFromScreen}
                  current={config.screenRef}
                  have={columns}
                  onAdd={(picked) => {
                    const next = copyColumns(columns, picked);
                    if (next.columns.length !== columns.length) onConfigChange({ columns: next.columns });
                  }}
                />
              )}
              {/* The fourth way a column arrives, and the one for members: a
                  reference that stays in step with the group's screen. */}
              {screenLinks && (
                <LinkFromScreen
                  kind="columns"
                  source={screenLinks}
                  categoryOf={(field) => meta?.find((m) => m.field === field)?.category}
                  current={config.screenRef}
                  have={columns}
                  onLink={(picked, screen) => {
                    const next = linkColumns(columns, picked, screen);
                    if (next.columns.length !== columns.length) onConfigChange({ columns: next.columns });
                  }}
                />
              )}
            </>
          ) : undefined
        }
        timings={timings}
        showTimings={showTimings}
        onToggleTimings={onToggleTimings}
        onSimulate={onSimulate}
        simulating={simulating}
      />
      {/* Colouring one column by an expression moved here from Format (owner,
          2026-10-06): it is per column, like the precision and notation beside
          it, and in a tab about rules for many columns it read as one of them.
          Folded by default — it overrides every rule on the column. */}
      {selected && (
        <details
          style={{ ...editorPanel, marginTop: "0.5rem" }}
          // Mounted only while open: a closed <details> still renders its
          // children, and a second ExprCell on the tab is a second set of the
          // same controls for anyone (or anything) looking for the first.
          onToggle={(e) => setColorByOpen((e.currentTarget as HTMLDetailsElement).open)}
          open={colorByOpen}
        >
          <summary style={{ cursor: "pointer", fontSize: "0.76rem", color: "var(--qf-text-secondary)" }}>
            Colour this column by an expression{" "}
            {columns.find((c) => c.field === selected)?.colorBy ? "(on — overrides its Format rules)" : ""}
          </summary>
          {colorByOpen && (
          <div style={{ marginTop: "0.4rem" }}>
            <ExprCell
              value={columns.find((c) => c.field === selected)?.colorBy}
              meta={meta}
              compile={compileExpr}
              allowBoolean
              onChange={(colorBy: ScreenerExpr) => patch(selected, { colorBy })}
            />
            {columns.find((c) => c.field === selected)?.colorBy && (
              <button
                type="button"
                onClick={() => patch(selected, { colorBy: undefined })}
                style={{ ...smallButton, marginTop: "0.3rem" }}
              >
                Remove expression
              </button>
            )}
          </div>
          )}
        </details>
      )}
      </div>
    </div>
  );
}

/**
 * The expression editor, below the list rather than inside a row.
 *
 * One editor bound to whichever computed column is selected. An expression is
 * the most substantial thing on this tab — it needs room to read, a place to
 * show a compile error against, and space for the structured controls to lay out
 * — and a table cell gives it none of those.
 *
 * Nothing is applied on a button: the builder emits config patches, so a valid
 * expression is live in the grid as soon as it compiles.
 */
function ExpressionEditor({
  column,
  meta,
  compileExpr,
  dslCatalog,
  describeExpr,
  describeColumns,
  onChange,
  onRollupChange,
  onDelete,
  onAdd,
  onAddLogo,
  selectedField,
  onPromote,
  copySlot,
  timings,
  showTimings,
  onToggleTimings,
  onSimulate,
  simulating,
}: {
  column: ScreenerColumn | undefined;
  meta: readonly ScreenerColumnMeta[];
  compileExpr?: CompileExpr;
  dslCatalog?: DslCatalog;
  describeExpr?: DescribeExpr;
  describeColumns: readonly DescribeColumn[];
  onChange: (expr: ScreenerExpr, source?: string) => void;
  onRollupChange: (rollup: "recompute" | "aggregate" | "none") => void;
  onDelete: () => void;
  onAdd: () => void;
  /** Add the logo column; omitted when the screen has one or cannot draw one. */
  onAddLogo?: () => void;
  /** The row the list has selected, even when it has no expression. */
  selectedField?: string | null;
  /** Give a server column an expression, seeded with itself. */
  onPromote?: (field: string) => void;
  /** **Copy from screen…**, when the host offers it; drawn beside + Logo. */
  copySlot?: ReactNode;
  timings?: ScreenerTimings;
  showTimings: boolean;
  onToggleTimings: () => void;
  onSimulate?: () => void;
  simulating?: boolean;
}) {
  if (!column?.computed) {
    return (
      <div style={editorPanel}>
        <div style={editorHeader}>
          {/* **Two empty states, because they are two different situations.**
              Nothing selected is an instruction. A *server* column selected is
              not — the reader has picked a row and is being told to pick a row,
              which is what sent somebody hunting for the one cell that worked.
              Its value is the field, so the only thing this panel can offer is
              the way to give it an expression, and it offers exactly that. */}
          {selectedField ? (
            <>
              <span style={{ color: "var(--qf-text-muted)" }}>
                <code>{selectedField}</code> comes from the screen, so it has no
                expression.
              </span>
              {onPromote && (
                <button onClick={() => onPromote(selectedField)} style={smallButton}>
                  Compute it from an expression
                </button>
              )}
            </>
          ) : (
            <span style={{ color: "var(--qf-text-muted)" }}>
              Select a column above to edit its expression.
            </span>
          )}
          <span style={{ marginLeft: "auto" }} />
          <PerfButtons
            timings={timings}
            showTimings={showTimings}
            onToggleTimings={onToggleTimings}
            onSimulate={onSimulate}
            simulating={simulating}
          />
          {/* Here too (owner, 2026-10-06), or a screen with no computed column
              never sees it — "+ Column" only shows while one is selected. */}
          {onAddLogo && (
            <button onClick={onAddLogo} style={smallButton} title="Show the company logo beside Symbol">
              + Logo
            </button>
          )}
          {copySlot}
          <button onClick={onAdd} style={primaryButton}>
            + Computed column
          </button>
        </div>
        {showTimings && (
          <ScreenerTimingsPanel timings={timings} onClose={onToggleTimings} />
        )}
      </div>
    );
  }

  const history = needsHistory(column.computed.expression);

  return (
    <div style={editorPanel}>
      <div style={editorHeader}>
        <strong style={{ fontSize: "0.8rem" }}>{column.header || column.field}</strong>
        <span style={{ fontSize: "0.72rem", color: "var(--qf-text-muted)" }}>
          {/* Where it is computed is the one fact that explains a slow first
              load, so it is stated rather than left to be discovered. */}
          {history ? "computed on the server (needs bar history)" : "computed in the browser"}
        </span>
        <label style={{ marginLeft: "auto", fontSize: "0.72rem", color: "var(--qf-text-secondary)" }}>
          On rollups:{" "}
          <select
            value={column.computed.rollup ?? "recompute"}
            onChange={(e) => onRollupChange(e.target.value as "recompute" | "aggregate" | "none")}
            style={selectStyle}
          >
            <option value="recompute">recompute</option>
            <option value="aggregate">average</option>
            <option value="none">omit</option>
          </select>
        </label>
        <button
          onClick={onDelete}
          title="Delete this computed column"
          style={{ ...smallButton, color: "var(--qf-negative)" }}
        >
          Delete
        </button>
          <PerfButtons
            timings={timings}
            showTimings={showTimings}
            onToggleTimings={onToggleTimings}
            onSimulate={onSimulate}
            simulating={simulating}
          />
        {/* Beside "+ Column" (owner, 2026-10-06): one click for a screen saved
            without a logo, instead of finding the hidden "Logo" row above. */}
        {onAddLogo && (
          <button onClick={onAddLogo} style={smallButton} title="Show the company logo beside Symbol">
            + Logo
          </button>
        )}
        {copySlot}
        <button onClick={onAdd} style={primaryButton} title="Add another computed column">
          + Column
        </button>
      </div>
      {showTimings && (
        <ScreenerTimingsPanel timings={timings} onClose={onToggleTimings} />
      )}

      <ExprCell
        value={column.computed.expression}
        source={column.computed.source}
        compile={compileExpr}
        catalog={dslCatalog}
        describe={describeExpr}
        describeColumns={describeColumns}
        meta={meta}
        onChange={onChange}
      />
    </div>
  );
}

// ── Filters ─────────────────────────────────────────────────────────────────

/**
 * Coerce a typed filter value to what the column actually holds.
 *
 * `true`/`false` become booleans. Without this a boolean column could not be
 * filtered at all: the string `"true"` never equals the boolean `true`, so the
 * condition matched nothing and there was no way to type one that did.
 *
 * Numbers are parsed for the same reason; everything else stays text, which is
 * what a sector comparison needs.
 */

export function parseFilterValue(raw: string): string | number | boolean {
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  const n = Number(raw);
  return raw !== "" && !Number.isNaN(n) ? n : raw;
}

const isGroup = (n: ScreenerFilterNode): n is { op: "all" | "any"; children: ScreenerFilterNode[] } =>
  "children" in n;

/**
 * Set or remove a saved filter's category from what the box holds.
 *
 * The key is **deleted** when the box is empty rather than set to `undefined`:
 * the store collapses an empty optional on the way out, so writing the key would
 * make a save and its reload differ — the rule `Layout.hidden` already follows.
 * Emptying the box is also the only way to take a condition back out of a
 * category, so it has to mean that rather than being ignored.
 */
/** The ★ set or removed. Off is absent, never `false` — `toggleFavorite`'s rule. */
function withFavorite(filter: SavedScreenerFilter, on: boolean): SavedScreenerFilter {
  if (on) return { ...filter, favorite: true };
  const { favorite: _dropped, ...rest } = filter;
  return rest;
}

function withCategory(filter: SavedScreenerFilter, category: string): SavedScreenerFilter {
  const trimmed = category.trim();
  if (trimmed) return { ...filter, category: trimmed };
  const { category: _dropped, ...rest } = filter;
  return rest;
}

function FiltersTab({
  config,
  meta,
  onConfigChange,
  onSavedFiltersChange,
  canEditLibrary = true,
  presetGate,
  mintId,
  scanScript,
  copyFromScreen,
  sealedBy,
  screenLinks,
}: {
  config: ScreenerConfig;
  meta: readonly ScreenerColumnMeta[];
  onConfigChange: (patch: ScreenerConfigPatch) => void;
  onSavedFiltersChange?: (filters: SavedScreenerFilter[]) => void;
  canEditLibrary?: boolean;
  presetGate?: PresetGate;
  mintId: () => string;
  /** The ScanScript editor's compiler and reference, for a condition written directly. */
  scanScript?: ScanScriptTools;
  copyFromScreen?: CopyFromScreenBinding;
  sealedBy?: string;
  screenLinks?: ScreenLinks;
}) {
  const [name, setName] = useState("");
  /** Groups the filter in the picker, which is a tree once there are enough. */
  const [category, setCategory] = useState("");
  /** ★ — listed in the picker's Favourites section as well as its category. */
  const [favorite, setFavorite] = useState(false);
  /** The ★ and ↑↓ controls, and the box: only where the library can be written. */
  const arrange = canEditLibrary && !!onSavedFiltersChange;
  const saved = config.savedFilters ?? [];

  // Always edit a group, so adding a second condition never has to restructure
  // what the user already built.
  const root: ScreenerFilterNode =
    config.filter && !("kind" in config.filter)
      ? isGroup(config.filter)
        ? config.filter
        : { op: "all", children: [config.filter] }
      : { op: "all", children: [] };

  const setRoot = (next: ScreenerFilterNode) => onConfigChange({ filter: next });

  /**
   * The saved filter currently loaded, if any — what "Update" writes back to.
   *
   * **Never a sealed one.** Ticking one sealed condition alone sets
   * `activeFilterRef` to it (`applySelection`), and treating that as loaded
   * would offer *Update* — which writes the editor's tree into the entry, giving
   * the group's condition a rule somebody guessed. It is ticked, not loaded.
   */
  // Nor a linked one, for the same reason: its rule is its source screen's.
  const editing = saved.find((f) => f.id === config.activeFilterRef && !isSealed(f) && !f.link);

  /**
   * The two boxes follow whatever condition is loaded.
   *
   * **Because otherwise a saved condition can never be renamed.** `Update` wrote
   * only the AST, the boxes were cleared the moment one was saved, and typing a
   * name into an empty box and pressing `Save as new` makes a duplicate rather
   * than a rename — so a name or a category, once chosen, was permanent. There
   * is no rename in the picker either: its ⋮ offers Edit and Delete.
   *
   * It also gives `Update` something to show for itself. Rewriting an expression
   * that is already on screen changes nothing visible, so a working button read
   * as a dead one; with the name in the box, renaming and pressing Update moves
   * the library row and the button's own label.
   *
   * Keyed on the loaded **id**, not on the filter, so typing in these boxes is
   * not overwritten on the next keystroke — and `undefined` (nothing loaded, or
   * `+ New`) clears them, which is what `startNew` wants anyway.
   */
  const loaded = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (editing?.id === loaded.current) return;
    loaded.current = editing?.id;
    setName(editing?.name ?? "");
    setCategory(editing?.category ?? "");
    setFavorite(!!editing?.favorite);
  }, [editing?.id, editing?.name, editing?.category, editing?.favorite]);

  /** Tick several to apply them ANDed; see `applySelection`. */
  const applyIds = (ids: string[]) => onConfigChange(applySelection(config, ids));

  const startNew = () => {
    onConfigChange({
      filter: { op: "all", children: [] },
      // Cleared, not left pointing at what was loaded: `editing` reads it, and a
      // stale ref would offer "Update <old name>" over conditions that are no
      // longer that filter's.
      activeFilterRef: undefined,
      activeFilterRefs: [],
    });
    // The boxes follow `editing`, which this has just cleared — but the effect
    // runs after the patch lands, and a name left sitting in the box between
    // the two renders reads as "this sheet is still called that".
    setName("");
    setCategory("");
    setFavorite(false);
  };

  return (
    // The library on the left, the conditions on the right — the same shape the
    // Columns tab uses, for the same reason: what you pick and what you edit are
    // both on screen at once. A dropdown showed one filter at a time and could
    // not express a multi-selection at all, which the config has always
    // supported through `activeFilterRefs`.
    <div style={filtersLayout}>
      <div style={filtersLibrary}>
        <SavedFilterPicker
          inline
          filters={saved}
          categoryOrder={config.savedFilterCategoryOrder}
          selected={selectedFilterIds(config)}
          onChange={applyIds}
          describe={(f) => summarizeFilter(f.filter)}
          sealedBy={sealedBy}
          links={screenLinks}
          onAdd={startNew}
          onEdit={(f) =>
            onConfigChange({
              filter: f.filter,
              activeFilterRef: f.id,
              activeFilterRefs: [f.id],
            })
          }
          onToggleFavorite={
            arrange ? (f) => onSavedFiltersChange!(toggleFavorite(saved, f.id)) : undefined
          }
          onMoveCategory={
            arrange
              ? (cat, delta) =>
                  onConfigChange({
                    savedFilterCategoryOrder: moveCategory(
                      saved,
                      config.savedFilterCategoryOrder,
                      cat,
                      delta,
                    ),
                  })
              : undefined
          }
          onDelete={
            onSavedFiltersChange
              ? (f) => {
                  onSavedFiltersChange(saved.filter((x) => x.id !== f.id));
                  // Applied filters are ids, so deleting one that is ticked
                  // would leave the config naming a filter that is gone.
                  if (selectedFilterIds(config).includes(f.id)) {
                    applyIds(selectedFilterIds(config).filter((id) => id !== f.id));
                  }
                }
              : undefined
          }
        />
      </div>

      <div style={filtersConditions}>
      {/*
        **The two verbs on top, the two fields under them, each behind a label.**

        They were one wrapping row — `[Update X] [name] [category] [Save as new]`
        — where the buttons sat *between* and *after* the boxes they act on, the
        boxes were told apart only by placeholder text that vanishes the moment
        you type into them, and a narrow pane wrapped the row at whatever point
        the widths happened to fall. So which button committed which box was
        something you worked out by trying it.

        Labels rather than placeholders because a placeholder is not a label: it
        is gone exactly when the field has content, which is when somebody
        coming back to a half-filled form needs it most. `Cat` and not
        `Category`, so the two labels are close enough in width for the boxes to
        line up without a grid.
      */}
      <Toolbar>
        {/*
          Updating an existing filter, which had no way to happen at all: you
          could load one, change a condition, and the only button offered was
          "Save as new" — so an edit either became a duplicate or was lost on
          the next load. Shown only when one is loaded, since there is otherwise
          nothing to update.

          First in the row, because it is the one that acts on the condition
          already on screen; `Save as new` follows it as the escape hatch.
        */}
        {editing && onSavedFiltersChange && (
          <button
            onClick={() =>
              onSavedFiltersChange(
                // **Name and category too, not just the AST.** Those were
                // unreachable once a condition existed — see the boxes above.
                // An empty name box keeps the stored name rather than blanking
                // it: a nameless row in the picker is unclickable, and clearing
                // the box is not how anyone says "call it nothing".
                saved.map((f) =>
                  f.id === editing.id
                    ? withFavorite(
                        withCategory({ ...f, name: name.trim() || f.name, filter: root }, category),
                        arrange ? favorite : !!f.favorite,
                      )
                    : f,
                ),
              )
            }
            title={`Save the name, category and conditions back into "${editing.name}"`}
            style={primaryButton}
          >
            Update {editing.name}
          </button>
        )}
        <button
          disabled={!name.trim() || !onSavedFiltersChange}
          onClick={() => {
            if (!onSavedFiltersChange) return;
            const entry: SavedScreenerFilter = {
              id: mintId(),
              name: name.trim(),
              filter: root,
              ...(category.trim() ? { category: category.trim() } : {}),
              ...(arrange && favorite ? { favorite: true } : {}),
            };
            onSavedFiltersChange([...saved, entry]);
            onConfigChange({ activeFilterRef: entry.id });
            // The boxes are **not** cleared here any more. They now follow the
            // loaded condition, and this has just loaded the one it made — so
            // clearing them would blank the name of the thing on screen and
            // leave `Update` the only way to get it back.
          }}
          style={primaryButton}
        >
          Save as new
        </button>
        {/* The Columns tab's Copy from screen…, for the library: conditions
            arrive as named entries beside the screen's own, ticked by nobody. */}
        {copyFromScreen && onSavedFiltersChange && (
          <CopyFromScreen
            kind="conditions"
            source={copyFromScreen}
            current={config.screenRef}
            have={saved}
            onAdd={(picked) => {
              const next = copyConditions(saved, picked);
              if (next.savedFilters.length !== saved.length) onSavedFiltersChange(next.savedFilters);
            }}
          />
        )}
        {/* A link, not a copy: the rule stays the group's and is applied on the
            server, so a sealed condition may be linked where it may not be
            copied. Arrives unticked, like a copied one. */}
        {screenLinks && onSavedFiltersChange && (
          <LinkFromScreen
            kind="conditions"
            source={screenLinks}
            current={config.screenRef}
            have={saved}
            onLink={(picked, screen) => {
              const next = linkConditions(saved, picked, screen);
              if (next.savedFilters.length !== saved.length) onSavedFiltersChange(next.savedFilters);
            }}
          />
        )}
      </Toolbar>

        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Condition name"
            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
          />
        </Field>
        {/* Free text with the existing ones offered: a fixed list would need
            managing before the first filter could be saved, and a category is
            only worth having if adding one costs nothing. */}
        <Field label="Cat">
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            list="qf-filter-categories"
            aria-label="Condition category"
            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
          />
        </Field>
        {arrange && (
          <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem" }}>
            <input
              type="checkbox"
              checked={favorite}
              onChange={(e) => setFavorite(e.target.checked)}
              aria-label="Favourite"
            />
            ★ Favourite
          </label>
        )}
        <datalist id="qf-filter-categories">
          {categoriesOf(saved).map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        <GroupEditor node={root} meta={meta} onChange={setRoot} scanScript={scanScript} />
        {/* Under the box it describes, with AND/OR spelled out — beside Save
            it read as part of the toolbar. */}
        <Hint>Reads as: {summarizeFilter(root) || "every row"}</Hint>

        {/* Standard conditions (owner, 2026-10-26). + Add copies one into the
            library and loads it, so its numbers are the user's to change. */}
        {onSavedFiltersChange && (
          <div style={{ ...presetBox }}>
            <strong style={{ fontSize: "0.78rem" }}>Presets</strong>
            <Hint>Standard conditions. + Add puts a copy in your list.</Hint>
            <ConditionPresetList
              filters={saved}
              onAdd={(preset) => {
                const copy = presetCopy(preset, mintId());
                onSavedFiltersChange([...saved, copy]);
                onConfigChange({ filter: copy.filter, activeFilterRef: copy.id, activeFilterRefs: [copy.id] });
              }}
              allowed={presetGate}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const presetBox: React.CSSProperties = {
  marginTop: "0.75rem",
  padding: "0.5rem 0.6rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  maxWidth: 420,
};

/** What a ScanScript condition row needs from the host — both optional, as for columns. */
interface ScanScriptTools {
  compile?: CompileExpr;
  catalog?: DslCatalog;
}

/**
 * A condition written directly in ScanScript (docs/plans/pattern-screener.md).
 * True or false per row; one that reads bar history is computed by the server
 * as a hidden field (`filter-eval.ts`), which the note says so a user knows why
 * it takes a moment.
 */
function ScanScriptCondition({
  value,
  meta,
  onChange,
  scanScript,
}: {
  value: ScreenerExpr;
  meta: readonly ScreenerColumnMeta[];
  onChange: (next: ScreenerExpr) => void;
  scanScript?: ScanScriptTools;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0, borderLeft: "2px solid var(--qf-accent)", paddingLeft: "0.4rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem" }}>
        <span title="A condition written in ScanScript" style={{ fontStyle: "italic", fontWeight: 600 }}>ƒ</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ExprCell
            value={value}
            meta={meta}
            allowBoolean
            compile={scanScript?.compile}
            catalog={scanScript?.catalog}
            onChange={(expr) => onChange(expr)}
          />
        </div>
      </div>
      {needsHistory(value) && (
        <div style={{ color: "var(--qf-text-muted)", fontSize: "0.72rem", marginTop: "0.15rem" }}>
          uses history — computed on the server
        </div>
      )}
    </div>
  );
}

function GroupEditor({
  node,
  meta,
  onChange,
  depth = 0,
  scanScript,
}: {
  node: ScreenerFilterNode;
  meta: readonly ScreenerColumnMeta[];
  onChange: (next: ScreenerFilterNode) => void;
  depth?: number;
  scanScript?: ScanScriptTools;
}) {
  if (!isGroup(node)) return null;

  const setChild = (i: number, next: ScreenerFilterNode) =>
    onChange({ ...node, children: node.children.map((c, j) => (j === i ? next : c)) });
  const remove = (i: number) =>
    onChange({ ...node, children: node.children.filter((_, j) => j !== i) });

  const add = (child: ScreenerFilterNode) => onChange({ ...node, children: [...node.children, child] });
  const nested = depth > 0;

  // The ALL/ANY choice is part of a sentence heading the box, so it reads as a
  // setting of the box rather than a fourth action beside the add buttons —
  // which is how the old `match all (AND)` dropdown was read.
  const opToggle = (
    <span role="group" aria-label="Match" style={segStyle}>
      {(["all", "any"] as const).map((op, i) => (
        <button
          key={op}
          aria-pressed={node.op === op}
          onClick={() => node.op !== op && onChange({ ...node, op })}
          style={{
            ...segButton,
            ...(i > 0 ? { borderLeft: "1px solid var(--qf-accent)" } : {}),
            ...(node.op === op ? segButtonOn : {}),
          }}
        >
          {op.toUpperCase()}
        </button>
      ))}
    </span>
  );

  return (
    <div style={nested ? nestedGroupBox : groupBox}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.3rem", fontWeight: 600 }}>
        {nested ? (
          <>Condition group — {opToggle} of these</>
        ) : (
          <>Show rows where {opToggle} of these are true</>
        )}
      </div>

      {node.children.length === 0 && (
        <div style={{ color: "var(--qf-text-muted)", fontSize: "0.76rem", marginBottom: "0.25rem" }}>
          No conditions — every row passes.
        </div>
      )}

      {node.children.map((child, i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.35rem", marginBottom: "0.25rem" }}>
          {"kind" in child ? (
            <ScanScriptCondition
              value={child as ScreenerExpr}
              meta={meta}
              onChange={(n) => setChild(i, n)}
              scanScript={scanScript}
            />
          ) : isGroup(child) ? (
            <GroupEditor
              node={child}
              meta={meta}
              onChange={(n) => setChild(i, n)}
              depth={depth + 1}
              scanScript={scanScript}
            />
          ) : (
            <ConditionEditor
              condition={child as ScreenerFilterCondition}
              meta={meta}
              onChange={(n) => setChild(i, n)}
            />
          )}
          <button onClick={() => remove(i)} title="Remove" style={smallButton}>
            ✕
          </button>
        </div>
      ))}

      {/* At the foot of each box, so a rule appears where it was asked for and
          a condition group has its own row. */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap", marginTop: "0.2rem" }}>
        <span style={addLabel}>Add</span>
        <button
          onClick={() => add({ field: "", op: "gt", value: 0 } as ScreenerFilterCondition)}
          style={smallButton}
          title="A column, a comparison and a value — e.g. nPA = true"
        >
          Column rule
        </button>
        {/* A condition written directly — no column needed first. Starts as
            `true` (passes everything) so adding one never empties the grid. */}
        <button
          onClick={() => add({ kind: "literal", value: true } as ScreenerExpr)}
          style={smallButton}
          title="A condition written in ScanScript — e.g. close > sma(close, 50)"
        >
          Formula
        </button>
        {/* Nesting is what Community's own model cannot express, so it is
            offered explicitly rather than left to chance. */}
        <button
          onClick={() => add({ op: "any", children: [] })}
          style={smallButton}
          title="A nested set with its own ALL/ANY — e.g. A and (B or C)"
        >
          Condition group
        </button>
      </div>
    </div>
  );
}

export const CONDITION_OPS = [
  "eq", "ne", "gt", "gte", "lt", "lte", "between",
  "contains", "notContains", "startsWith", "endsWith", "blank", "notBlank",
] as const;

/** What the comparison select shows; the stored `op` is unchanged. */
export const OP_LABEL: Record<(typeof CONDITION_OPS)[number], string> = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  between: "between",
  contains: "contains",
  notContains: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
  blank: "is blank",
  notBlank: "is not blank",
};

export function ConditionEditor({
  condition,
  meta,
  onChange,
}: {
  condition: ScreenerFilterCondition;
  meta: readonly ScreenerColumnMeta[];
  onChange: (next: ScreenerFilterCondition) => void;
}) {
  const needsValue = condition.op !== "blank" && condition.op !== "notBlank";
  const needsUpper = condition.op === "between";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", flexWrap: "wrap" }}>
      <FieldRef
        value={
          condition.field
            ? { field: condition.field, base: condition.field, frequency: "", offset: 0 }
            : undefined
        }
        meta={meta}
        onChange={(v) => onChange({ ...condition, field: v.field })}
      />
      {/* Suggestions, not a constraint: the condition editor does not know the
          column's type, and a boolean column is unfilterable if `true` cannot
          be typed. A datalist offers them where they apply and stays out of the
          way where they do not. */}
      <datalist id="qf-filter-values">
        <option value="true" />
        <option value="false" />
      </datalist>
      <select
        value={condition.op}
        onChange={(e) => onChange({ ...condition, op: e.target.value as ScreenerFilterCondition["op"] })}
        style={inputStyle}
      >
        {CONDITION_OPS.map((op) => (
          <option key={op} value={op}>
            {OP_LABEL[op]}
          </option>
        ))}
      </select>
      {needsValue && (
        <input
          value={String(condition.value ?? "")}
          list="qf-filter-values"
          onChange={(e) => onChange({ ...condition, value: parseFilterValue(e.target.value) })}
          style={{ ...inputStyle, width: 100 }}
        />
      )}
      {needsUpper && (
        <input
          value={String(condition.valueTo ?? "")}
          onChange={(e) => onChange({ ...condition, valueTo: parseFilterValue(e.target.value) })}
          style={{ ...inputStyle, width: 100 }}
        />
      )}
    </span>
  );
}

// ── chrome ──────────────────────────────────────────────────────────────────

const Toolbar = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.6rem", flexWrap: "wrap" }}>
    {children}
  </div>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
  <span style={{ fontSize: "0.72rem", color: "var(--qf-text-muted)" }}>{children}</span>
);

/**
 * A labelled field on its own line, the label in a fixed column so a stack of
 * them lines up without a grid.
 *
 * The label is a real `<label>` wrapping its control, so clicking the word
 * focuses the box and a screen reader reads the pair — which is the half a
 * placeholder cannot do at all, on top of vanishing as soon as anything is
 * typed. Each control keeps its own `aria-label` anyway, since the tests and
 * the two boxes' distinct names are worth having either way.
 */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      marginBottom: "0.35rem",
      fontSize: "0.72rem",
      color: "var(--qf-text-secondary)",
    }}
  >
    <span style={{ flex: "0 0 2.4rem" }}>{label}</span>
    {children}
  </label>
);

const Th = ({ children, w }: { children?: React.ReactNode; w?: number }) => (
  <th
    style={{
      width: w,
      textAlign: "left",
      fontWeight: 500,
      color: "var(--qf-text-muted)",
      fontSize: "0.72rem",
      padding: "0.2rem 0.3rem",
      // Sticks to the top of the scrolling list, as the data grid's header
      // does. A long column list otherwise scrolls its own headings away and
      // leaves five unlabelled inputs per row.
      //
      // On the `th` rather than the row or the `thead`: `position: sticky` does
      // not apply to those in every browser, and a background is required or
      // the rows show through as they pass under it.
      position: "sticky",
      top: 0,
      zIndex: 1,
      background: "var(--qf-surface-alt)",
      boxShadow: "inset 0 -1px 0 var(--qf-border)",
    }}
  >
    {children}
  </th>
);

const Td = ({ children }: { children?: React.ReactNode }) => (
  // `overflow: hidden` is what makes the fixed layout actually clip rather than
  // spill its content over the next cell.
  <td style={{ padding: "0.2rem 0.3rem", verticalAlign: "top", overflow: "hidden" }}>
    {children}
  </td>
);

const backdrop: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 40,
  background: "rgba(4,6,10,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const panel: React.CSSProperties = {
  width: "min(96%, 900px)",
  maxHeight: "88%",
  display: "flex",
  flexDirection: "column",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};

const header: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "0.45rem 0.6rem",
  borderBottom: "1px solid var(--qf-border)",
};

/** Docked: fill the column, no floating chrome, no rounded card edges. */
const embeddedPanel: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};

/**
 * `flex: 1` so the tab fills the panel and a child can size itself as a share of
 * it. Without a definite height here, the Columns tab's 75/25 split has nothing
 * to be 75%% *of* and both panes collapse to their content.
 *
 * `overflow: hidden`, not `auto`: the Columns tab scrolls its list pane so the
 * editor stays pinned. A scrollbar here as well would scroll the editor away —
 * exactly what the split exists to prevent. The other two tabs scroll their own
 * content.
 */
const body: React.CSSProperties = {
  padding: "0.6rem",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

/**
 * `table-layout: fixed`, so a long expression cannot widen its column.
 *
 * With the default `auto`, a cell's content sizes the column — one
 * `(h - l) / c * 100 and v > v.sma20 * 2` stretched the DSL column and squeezed
 * everything else off screen. Fixed layout makes the declared widths the
 * widths, and lets the cell clip instead. Clipping is safe here because the
 * tooltip carries the whole expression and the editor below shows it in full.
 */
/**
 * The declared widths, named so `table.minWidth` can be derived from them.
 *
 * Inline literals in the header would drift: raising one without raising the
 * minimum silently reintroduces the squeeze below.
 */
/** The Format cell outside ✎ Edit: blank for a plain number. */
function notationShort(notation: ScreenerColumn["notation"]): string {
  switch (notation) {
    case "compact":
      return "K / M / B";
    case "thousands":
      return "K";
    case "millions":
      return "M";
    case "billions":
      return "B";
    default:
      return "";
  }
}

const COLUMN_WIDTHS = {
  grip: 22,
  name: 150,
  header: 150,
  band: 120,
  column: 140,
  width: 70,
  pin: 80,
  decimals: 70,
  scale: 110,
} as const;

/**
 * The floor for the DSL column, which is the only unsized one.
 *
 * Enough for a short expression plus the ellipsis that says there is more —
 * `c.rankW > 90 and c.…`. The tooltip and the editor below carry the whole
 * thing, so this is a "you can tell which column this is" width, not a "you can
 * read the expression" one.
 */
const DSL_MIN_WIDTH = 220;

/**
 * `table-layout: fixed`, and a `min-width` so the panel scrolls rather than
 * crushing one column.
 *
 * Every column but DSL declares a width, so DSL absorbs all the slack — which
 * also makes it the only one that shrinks when the panel narrows. Docked on the
 * side at a comfortable width it went to nothing, and the DSL cell is both the
 * one you read and (until the row became clickable) the only way to open the
 * editor.
 *
 * `width: 100%` still applies above the minimum, so DSL keeps taking the extra
 * on a wide panel. Below it the table overflows and `listPane`'s `overflow:
 * auto` scrolls horizontally.
 */
/** Library beside conditions — the Columns tab's split, applied to filters. */
const filtersLayout: React.CSSProperties = {
  display: "flex",
  gap: "0.6rem",
  flex: 1,
  minHeight: 0,
};

const filtersLibrary: React.CSSProperties = {
  flex: "0 0 240px",
  minHeight: 0,
  display: "flex",
};

const filtersConditions: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
};

const table: React.CSSProperties = {
  width: "100%",
  // `grip` is rendered **twice** — once leading, once trailing — so it is
  // counted twice here. Deriving the floor from the map is what keeps the two
  // in step; forgetting the second one understates it by exactly one grip and
  // the DSL column silently loses that much room.
  minWidth:
    Object.values(COLUMN_WIDTHS).reduce((sum, w) => sum + w, 0) +
    COLUMN_WIDTHS.grip +
    DSL_MIN_WIDTH,
  borderCollapse: "collapse",
  tableLayout: "fixed",
};

const tabButton: React.CSSProperties = {
  border: "none",
  borderRadius: 4,
  padding: "0.25rem 0.7rem",
  cursor: "pointer",
  fontSize: "0.8rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
};

const primaryButton: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.2rem 0.55rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.76rem",
};

/** A one-line, clickable rendering of an expression in the columns table. */
const idCell: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.72rem",
  color: "var(--qf-text-secondary)",
  whiteSpace: "nowrap",
};

const exprSummary: React.CSSProperties = {
  display: "block",
  width: "100%",
  maxWidth: "100%",
  textAlign: "left",
  padding: "0.15rem 0.35rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.72rem",
  cursor: "pointer",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** A tab that scrolls as one column. */
const scrollPane: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto" };

const columnsLayout: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
};

/** The list: three quarters, and the only part that scrolls. */
const listPane: React.CSSProperties = {
  flex: "3 1 0",
  minHeight: 0,
  overflow: "auto",
};

/**
 * The grab area, deliberately taller than the line it draws.
 *
 * `flex: none` because it sits between two flexible panes and must not take a
 * share of the space it divides. `touchAction: none` or a drag on a touch
 * device scrolls the list instead of moving the divider.
 */
const splitHandle: React.CSSProperties = {
  flex: "none",
  height: 9,
  margin: "1px 0",
  cursor: "row-resize",
  touchAction: "none",
  display: "flex",
  alignItems: "center",
};

/** The line itself — one pixel, so the handle reads as a seam and not a bar. */
const splitGrip: React.CSSProperties = {
  width: "100%",
  height: 1,
  background: "var(--qf-border)",
};

/** The editor: the rest of the height, always visible. */
const editorPane: React.CSSProperties = {
  flex: "1 1 0",
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
};

const editorPanel: React.CSSProperties = {
  marginTop: "0.7rem",
  padding: "0.55rem 0.6rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-surface-alt)",
  fontSize: "0.78rem",
};

/** Matches the selects inside `<ExprCell>`, so the panel reads as one control. */
const selectStyle: React.CSSProperties = {
  padding: "0.1rem 0.2rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.74rem",
};

const editorHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.45rem",
  flexWrap: "wrap",
};

const smallButton: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.05rem 0.35rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  fontSize: "0.74rem",
};

const inputStyle: React.CSSProperties = {
  padding: "0.15rem 0.3rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};

const groupBox: React.CSSProperties = {
  padding: "0.35rem 0.45rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  marginBottom: "0.35rem",
};

/** Dashed with an accent edge, so where a condition group starts and ends is visible. */
const nestedGroupBox: React.CSSProperties = {
  ...groupBox,
  flex: 1,
  minWidth: 0,
  border: "1px dashed var(--qf-border)",
  borderLeft: "3px solid var(--qf-accent)",
  fontSize: "0.76rem",
};

const segStyle: React.CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--qf-accent)",
  borderRadius: 4,
  overflow: "hidden",
};

const segButton: React.CSSProperties = {
  border: 0,
  padding: "0.05rem 0.45rem",
  background: "transparent",
  color: "var(--qf-accent)",
  fontWeight: 600,
  fontSize: "0.72rem",
  cursor: "pointer",
};

const segButtonOn: React.CSSProperties = {
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
};

const addLabel: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};


/**
 * The `i` and, for an admin, `Sim` — beside the add button at the foot of the
 * editor, which is the panel that already explains the screen to you.
 *
 * Deliberately **not** in the group bar: that row's chip layout is computed from
 * measured widths against an off-screen ruler, so a control added there changes
 * the arithmetic that decides how many universes fit on screen.
 *
 * `i` appears only when the server actually sent timings — a build that does not
 * report them should show no control rather than a button that opens an empty
 * panel. `Sim` appears only for an admin, for the same reason: a control nobody
 * may use is worse than one that is not there.
 */
function PerfButtons({
  timings,
  showTimings,
  onToggleTimings,
  onSimulate,
  simulating,
}: {
  timings?: ScreenerTimings;
  showTimings: boolean;
  onToggleTimings: () => void;
  onSimulate?: () => void;
  simulating?: boolean;
}) {
  return (
    <>
      {timings && (
        <button
          onClick={onToggleTimings}
          title={
            timings.universe === "rebuilt"
              ? "Where this screen's time went — the universe was rebuilt"
              : "Where this screen's time went"
          }
          aria-label="Where this screen's time went"
          aria-pressed={showTimings}
          style={{
            ...smallButton,
            fontStyle: "italic",
            fontWeight: 700,
            minWidth: "1.4rem",
            // A rebuild is the finding, so the button says so before it is
            // pressed — otherwise the one request worth inspecting looks like
            // every other one.
            color:
              timings.universe === "rebuilt"
                ? "var(--qf-negative)"
                : "var(--qf-text-secondary)",
            background: showTimings ? "var(--qf-selection)" : "transparent",
          }}
        >
          i
        </button>
      )}
      {onSimulate && (
        <button
          onClick={onSimulate}
          disabled={simulating}
          title="Run a screener build against 10 concurrent chart readers, and report both"
          style={{ ...smallButton, opacity: simulating ? 0.6 : 1 }}
        >
          {simulating ? "Sim…" : "Sim"}
        </button>
      )}
    </>
  );
}

/** The drag handle in the Columns list: the only part of a row that starts a drag. */
const GRIP: React.CSSProperties = {
  cursor: "grab",
  color: "var(--qf-text-muted)",
  fontSize: "0.7rem",
  lineHeight: 1,
  userSelect: "none",
  display: "inline-block",
};
